import { useState, lazy, Suspense, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight, Lock, CheckCircle2, XCircle, Sparkles, Bot, TrendingUp,
  Gauge, ShieldCheck, Clock, PlayCircle, ChevronDown, Rocket,
  Zap, Activity, Target, Brain, Sun, Moon,
} from "lucide-react";
import { useTheme } from "../context/ThemeContext";

const HeroScene = lazy(() => import("../components/velox/HeroScene"));

const LOGO = "https://customer-assets.emergentagent.com/job_admin-dashboard-flux/artifacts/ef1ryefo_The%20Gowda%E2%80%99s%20Group.png";

const PARTNERS = [
  { name: "EXNESS", url: "https://one.exnessonelink.com/a/0m8jl2kaza", note: "Recommended" },
  { name: "XM",      url: "https://affs.click/TtQdb",                    note: "Code: G42J6" },
  { name: "VANTAGE", url: "https://vigco.co/la-com-inv/Veloxalgo",       note: "Partner" },
];

const INDIAN_BROKERS = [
  { name: "ZERODHA KITE", note: "API" },
  { name: "ANGEL ONE",    note: "API" },
  { name: "UPSTOX",       note: "API" },
  { name: "FYERS",        note: "API" },
  { name: "GROWW",        note: "API" },
  { name: "ICICI",        note: "API" },
  { name: "KOTAK",        note: "API" },
];

const fadeUp = {
  initial: { opacity: 0, y: 36 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.65, ease: [0.22, 1, 0.36, 1] },
};

function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const { theme, toggle: toggleTheme } = useTheme();
  const isLight = theme === "light";
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return (
    <header
      data-testid="nav-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-[#0b132b]/85 backdrop-blur-xl border-b border-white/5" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto flex items-center justify-between px-6 lg:px-10 py-4">
        <Link to="/" className="flex items-center gap-3" data-testid="nav-logo-mark">
          <img src={LOGO} alt="VeloxAlgoTech LLP" className="w-12 h-12 rounded-full ring-1 ring-[#00b4d8]/30" />
          <span
            className="text-xs md:text-sm font-extrabold tracking-[0.14em] leading-[1.1] flex flex-col"
            style={{ fontFamily: "'Outfit', sans-serif" }}
            data-testid="brand-name-nav"
          >
            <span style={{ color: '#00b4d8' }}>Velox</span>
            <span className="text-white">AlgoTech</span>
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-9 text-[13px] text-zinc-400">
          <a href="#how" className="hover:text-white transition-colors" data-testid="nav-link-how">How it works</a>
          <a href="#why" className="hover:text-white transition-colors" data-testid="nav-link-why">Why Velox</a>
          <a href="#strategies" className="hover:text-white transition-colors" data-testid="nav-link-features">Strategies</a>
          <a href="#brokers" className="hover:text-white transition-colors" data-testid="nav-link-brokers">Brokers</a>
          <a href="#faq" className="hover:text-white transition-colors" data-testid="nav-link-faq">FAQ</a>
        </nav>

        <div className="flex items-center gap-2">
          {/* Theme toggle */}
          <button
            onClick={toggleTheme}
            data-testid="theme-toggle-landing"
            aria-label={isLight ? "Switch to dark theme" : "Switch to light theme"}
            className="w-9 h-9 rounded-full flex items-center justify-center transition-all border"
            style={{
              background: "var(--velox-panel)",
              borderColor: "var(--velox-border-strong)",
              color: "var(--velox-accent-cyan)",
            }}
          >
            {isLight ? <Moon size={15} /> : <Sun size={15} />}
          </button>

          <Link
            to="/login"
            data-testid="nav-cta-admin"
            className="hidden sm:inline-flex items-center px-4 py-2 text-[13px] font-medium text-zinc-300 hover:text-white rounded-full border border-white/10 hover:border-white/25 transition-colors"
          >
            Admin
          </Link>
          <Link
            to="/affiliate/login"
            data-testid="nav-cta-affiliate"
            className="inline-flex items-center gap-1.5 px-4 py-2 text-[13px] font-semibold rounded-full bg-[#00b4d8] text-[#0b132b] hover:bg-[#0077b6] hover:text-white transition-colors"
          >
            Login User
            <ArrowRight size={14} />
          </Link>
        </div>
      </div>
    </header>
  );
}

function Hero() {
  const navigate = useNavigate();
  return (
    <section
      data-testid="hero-section"
      className="relative min-h-screen pt-28 pb-16 overflow-hidden"
      style={{
        background:
          "radial-gradient(ellipse at 70% 30%, rgba(125,211,252,0.08), transparent 55%), #0b132b",
      }}
    >
      {/* Grain overlay */}
      <div
        aria-hidden
        className="absolute inset-0 opacity-[0.035] pointer-events-none mix-blend-overlay"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='200' height='200'><filter id='n'><feTurbulence baseFrequency='0.9' numOctaves='3' stitchTiles='stitch'/></filter><rect width='200' height='200' filter='url(%23n)' opacity='0.9'/></svg>\")",
        }}
      />

      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-10 px-6 lg:px-10 items-center relative">
        {/* Left column: copy */}
        <div className="lg:col-span-6 flex flex-col gap-7 z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="flex items-center gap-2 self-start px-3 py-1.5 rounded-full border border-[#00b4d8]/25 bg-[#00b4d8]/5"
            data-testid="hero-overline"
          >
            <Sparkles size={12} className="text-[#00b4d8]" />
            <span className="text-[10px] font-semibold tracking-[0.25em] text-[#00b4d8]" style={{ fontFamily: "'IBM Plex Mono', monospace" }}>
              AI-POWERED AUTOMATED FOREX &amp; INDIAN MARKET
            </span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.1 }}
            data-testid="hero-h1"
            className="text-5xl md:text-6xl lg:text-7xl font-extrabold tracking-tighter leading-[0.95] text-white"
            style={{ fontFamily: "'Outfit', sans-serif" }}
          >
            Trade smarter,
            <br />
            <span className="bg-gradient-to-r from-[#0077b6] via-[#00b4d8] to-[#38BDF8] bg-clip-text text-transparent">
              not harder.
            </span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-base md:text-lg text-zinc-400 max-w-xl leading-relaxed"
            style={{ fontFamily: "'Manrope', sans-serif" }}
          >
            Deploy institution-grade algorithmic forex strategies in minutes —
            no code, no emotion, no sleep required. Let AI execute, 24 / 5.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="flex flex-wrap items-center gap-3 mt-2"
          >
            <button
              onClick={() => navigate("/affiliate/login")}
              data-testid="hero-cta-demo"
              className="group inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-[#00b4d8] hover:bg-[#0077b6] text-[#0b132b] font-semibold text-sm transition-all shadow-[0_0_40px_rgba(125,211,252,0.25)] hover:shadow-[0_0_60px_rgba(125,211,252,0.4)]"
            >
              Get Started
              <ArrowRight size={16} className="group-hover:translate-x-0.5 transition-transform" />
            </button>
            <a
              href="#how"
              data-testid="hero-cta-watch"
              className="inline-flex items-center gap-2 px-5 py-3.5 rounded-full border border-white/15 hover:border-white/40 text-zinc-200 text-sm font-medium transition-colors"
            >
              <PlayCircle size={16} />
              See how it works
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 0.5 }}
            className="flex flex-wrap items-center gap-5 pt-4 text-[11px] text-zinc-500"
            style={{ fontFamily: "'IBM Plex Mono', monospace" }}
          >
            <span className="flex items-center gap-1.5"><ShieldCheck size={12} className="text-emerald-500" /> SECURE BROKER API</span>
            <span className="flex items-center gap-1.5"><Clock size={12} className="text-[#00b4d8]" /> 24/5 EXECUTION</span>
            <span className="flex items-center gap-1.5"><Bot size={12} className="text-[#00b4d8]" /> NO CODE</span>
          </motion.div>
        </div>

        {/* Right column: 3D canvas */}
        <div className="lg:col-span-6 h-[52vh] lg:h-[78vh] relative">
          <div className="absolute inset-0 rounded-3xl overflow-hidden border border-white/5 bg-gradient-to-br from-[#13131A]/40 to-transparent">
            <Suspense fallback={<div className="w-full h-full flex items-center justify-center text-zinc-600 text-xs" style={{ fontFamily: "'IBM Plex Mono', monospace" }}>LOADING ENGINE…</div>}>
              <HeroScene />
            </Suspense>
          </div>
          {/* Floating corner labels */}
          <div className="absolute top-4 left-4 px-2.5 py-1 rounded-md bg-[#0b132b]/70 backdrop-blur-md border border-white/10">
            <span className="text-[9px] tracking-[0.22em] text-[#00b4d8]" style={{ fontFamily: "'IBM Plex Mono', monospace" }}>● LIVE ENGINE</span>
          </div>
          <div className="absolute bottom-4 right-4 px-2.5 py-1 rounded-md bg-[#0b132b]/70 backdrop-blur-md border border-white/10">
            <span className="text-[9px] tracking-[0.22em] text-zinc-400" style={{ fontFamily: "'IBM Plex Mono', monospace" }}>MTF AI · SCALPING AI</span>
          </div>
        </div>
      </div>
    </section>
  );
}

function BrokersStrip() {
  return (
    <section
      id="brokers"
      data-testid="brokers-strip"
      className="py-14 border-y border-white/5 bg-gradient-to-r from-transparent via-white/[0.02] to-transparent"
    >
      <div className="max-w-6xl mx-auto px-6 lg:px-10 flex flex-col gap-10">
        {/* Header row */}
        <div className="flex items-center justify-center gap-2.5">
          <Lock size={14} className="text-[#00b4d8]" />
          <span className="text-[10px] tracking-[0.28em] text-zinc-400" style={{ fontFamily: "'IBM Plex Mono', monospace" }}>
            SECURE BROKER API INTEGRATION
          </span>
        </div>

        {/* Forex brokers row */}
        <div className="flex flex-col md:flex-row items-center gap-4 md:gap-6 justify-between">
          <div className="flex items-center gap-2 min-w-[120px]">
            <span className="text-[10px] tracking-[0.28em] text-[#00b4d8]" style={{ fontFamily: "'IBM Plex Mono', monospace" }}>
              FOREX
            </span>
          </div>
          <div className="flex items-center gap-3 md:gap-4 flex-wrap justify-center flex-1">
            {PARTNERS.map((b) => (
              <a
                key={b.name}
                href={b.url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`broker-chip-${b.name.toLowerCase()}`}
                className="group flex items-center gap-2 px-4 py-2 rounded-full border border-white/10 hover:border-[#00b4d8]/50 bg-white/[0.02] hover:bg-[#00b4d8]/[0.06] transition-all"
              >
                <span className="text-[11px] tracking-[0.25em] text-zinc-200 group-hover:text-white font-semibold" style={{ fontFamily: "'IBM Plex Mono', monospace" }}>
                  {b.name}
                </span>
                <span className="text-[9px] text-zinc-500 group-hover:text-[#00b4d8]" style={{ fontFamily: "'IBM Plex Mono', monospace" }}>
                  {b.note}
                </span>
                <ArrowRight size={11} className="text-zinc-500 group-hover:text-[#00b4d8] group-hover:translate-x-0.5 transition-all" />
              </a>
            ))}
          </div>
        </div>

        {/* Indian brokers row */}
        <div className="flex flex-col md:flex-row items-center gap-4 md:gap-6 justify-between">
          <div className="flex items-center gap-2 min-w-[120px]">
            <span className="text-[10px] tracking-[0.28em] text-[#00b4d8]" style={{ fontFamily: "'IBM Plex Mono', monospace" }}>
              INDIAN MARKET
            </span>
          </div>
          <div className="flex items-center gap-3 md:gap-4 flex-wrap justify-center flex-1">
            {INDIAN_BROKERS.map((b) => (
              <div
                key={b.name}
                data-testid={`indian-broker-chip-${b.name.toLowerCase().replace(/\s+/g, "-")}`}
                className="flex items-center gap-2 px-4 py-2 rounded-full border border-white/10 bg-white/[0.02]"
              >
                <span className="text-[11px] tracking-[0.25em] text-zinc-200 font-semibold" style={{ fontFamily: "'IBM Plex Mono', monospace" }}>
                  {b.name}
                </span>
                <span className="text-[9px] text-[#00b4d8]" style={{ fontFamily: "'IBM Plex Mono', monospace" }}>
                  {b.note}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

const STEPS = [
  { n: "01", title: "Connect broker", body: "Sign up and link Exness, XM, or Vantage in 90 seconds. Secure read/trade API — no withdrawal access.", icon: ShieldCheck },
  { n: "02", title: "Pick your bot", body: "MTF AI for USC/CENT accounts (1H + 30M, 9 & 27 + ATR), or Scalping AI for USD accounts ($10K min, 9 & 21).", icon: Gauge },
  { n: "03", title: "Configure & deploy", body: "Set your lot size — 1% of micro capital for MTF, micro lots only for Scalping — and let the bot fire on the next signal.", icon: Bot },
  { n: "04", title: "Track & scale", body: "Real-time performance dashboard on web & mobile. Compound winners, retire losers, repeat.", icon: TrendingUp },
];

function HowItWorks() {
  return (
    <section id="how" data-testid="how-it-works-section" className="py-28 px-6 lg:px-10 relative">
      <div className="max-w-7xl mx-auto">
        <motion.div {...fadeUp} className="max-w-2xl mb-14">
          <div className="text-[10px] tracking-[0.28em] text-[#00b4d8] mb-4" style={{ fontFamily: "'IBM Plex Mono', monospace" }}>
            THE PROCESS
          </div>
          <h2 className="text-3xl md:text-5xl font-bold text-white tracking-tight leading-[1.05]" style={{ fontFamily: "'Outfit', sans-serif" }}>
            From sign-up to live trades in <span className="text-[#00b4d8]">under 5 minutes</span>.
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
          {STEPS.map((s, i) => (
            <motion.div
              key={s.n}
              data-testid={`step-card-${i + 1}`}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.6, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] }}
              className="group relative p-7 rounded-2xl border border-white/10 bg-gradient-to-br from-[#13131A]/80 to-[#0b132b]/40 backdrop-blur-sm hover:border-[#00b4d8]/40 transition-all duration-500"
            >
              <div className="flex items-start justify-between mb-6">
                <span className="text-[10px] tracking-[0.25em] text-[#00b4d8]/70" style={{ fontFamily: "'IBM Plex Mono', monospace" }}>
                  {s.n}
                </span>
                <div className="w-10 h-10 rounded-xl bg-[#00b4d8]/10 border border-[#00b4d8]/20 flex items-center justify-center group-hover:bg-[#00b4d8]/20 transition-colors">
                  <s.icon size={18} className="text-[#00b4d8]" />
                </div>
              </div>
              <h3 className="text-xl font-semibold text-white mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>{s.title}</h3>
              <p className="text-sm text-zinc-400 leading-relaxed" style={{ fontFamily: "'Manrope', sans-serif" }}>{s.body}</p>
              {/* subtle accent line */}
              <div className="absolute bottom-0 left-7 right-7 h-px bg-gradient-to-r from-transparent via-[#00b4d8]/0 to-transparent group-hover:via-[#00b4d8]/40 transition-all duration-500" />
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

function StrategyCards() {
  return (
    <section id="strategies" data-testid="strategy-section" className="py-28 px-6 lg:px-10 relative">
      <div className="max-w-7xl mx-auto">
        <motion.div {...fadeUp} className="mb-14 max-w-2xl">
          <div className="text-[10px] tracking-[0.28em] text-[#00b4d8] mb-4" style={{ fontFamily: "'IBM Plex Mono', monospace" }}>
            TWO BOTS · ONE EDGE
          </div>
          <h2 className="text-3xl md:text-5xl font-bold text-white tracking-tight leading-[1.05]" style={{ fontFamily: "'Outfit', sans-serif" }}>
            Choose your engine.
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* MTF AI */}
          <motion.div
            {...fadeUp}
            data-testid="strategy-card-mtf"
            className="relative p-10 rounded-3xl border border-white/10 bg-[#13131A]/60 backdrop-blur-sm overflow-hidden group"
          >
            <svg className="absolute inset-0 w-full h-full opacity-20 group-hover:opacity-40 transition-opacity" preserveAspectRatio="none">
              <defs>
                <pattern id="grid-mtf" width="32" height="32" patternUnits="userSpaceOnUse">
                  <path d="M 32 0 L 0 0 0 32" fill="none" stroke="#00b4d8" strokeWidth="0.4" />
                </pattern>
              </defs>
              <rect width="100%" height="100%" fill="url(#grid-mtf)" />
            </svg>
            <div className="relative">
              <div className="flex items-center justify-between mb-8">
                <span className="text-[11px] tracking-[0.3em] text-[#00b4d8]" style={{ fontFamily: "'IBM Plex Mono', monospace" }}>MTF AI</span>
                <Activity size={22} className="text-[#00b4d8]" />
              </div>
              <h3 className="text-3xl md:text-4xl font-bold text-white mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>
                Multi-Time-Frame AI
              </h3>
              <p className="text-xs tracking-[0.2em] text-zinc-500 mb-5" style={{ fontFamily: "'IBM Plex Mono', monospace" }}>
                REQUIRES · USC (CENT) ACCOUNT
              </p>
              <p className="text-sm text-zinc-400 leading-relaxed mb-6 max-w-md" style={{ fontFamily: "'Manrope', sans-serif" }}>
                Analyses the 1H and 30M time frames before pulling the trigger. Built around a 9 &amp; 27 strategy with a custom ATR for volatility-aware execution.
              </p>
              <ul className="space-y-2 text-sm text-zinc-300">
                <li className="flex items-start gap-2"><CheckCircle2 size={14} className="text-[#00b4d8] mt-0.5 flex-shrink-0" /><span>Strategy: 9 &amp; 27 EMA with custom ATR</span></li>
                <li className="flex items-start gap-2"><CheckCircle2 size={14} className="text-[#00b4d8] mt-0.5 flex-shrink-0" /><span>Confirms on 1H + 30M before entry</span></li>
                <li className="flex items-start gap-2"><CheckCircle2 size={14} className="text-[#00b4d8] mt-0.5 flex-shrink-0" /><span>Lot size = 1% of micro account capital</span></li>
              </ul>
            </div>
          </motion.div>

          {/* Scalping AI */}
          <motion.div
            {...fadeUp}
            data-testid="strategy-card-scalping"
            className="relative p-10 rounded-3xl border border-white/10 bg-gradient-to-br from-[#13131A]/80 to-[#0b132b]/40 backdrop-blur-sm overflow-hidden group"
          >
            <svg className="absolute inset-0 w-full h-full opacity-20 group-hover:opacity-50 transition-opacity" preserveAspectRatio="none" viewBox="0 0 400 300">
              <polyline points="0,260 40,210 70,230 110,180 150,200 190,150 230,170 270,110 310,140 350,80 400,100" fill="none" stroke="#0eae7a" strokeWidth="1.4" />
              <polyline points="0,260 40,210 70,230 110,180 150,200 190,150 230,170 270,110 310,140 350,80 400,100" fill="none" stroke="#00b4d8" strokeWidth="0.5" strokeDasharray="3 5" />
            </svg>
            <div className="relative">
              <div className="flex items-center justify-between mb-8">
                <span className="text-[11px] tracking-[0.3em] text-emerald-400" style={{ fontFamily: "'IBM Plex Mono', monospace" }}>SCALPING AI</span>
                <Zap size={22} className="text-emerald-400" />
              </div>
              <h3 className="text-3xl md:text-4xl font-bold text-white mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>
                High-frequency Scalper
              </h3>
              <p className="text-xs tracking-[0.2em] text-zinc-500 mb-5" style={{ fontFamily: "'IBM Plex Mono', monospace" }}>
                REQUIRES · USD ACCOUNT · MIN $10,000
              </p>
              <p className="text-sm text-zinc-400 leading-relaxed mb-6 max-w-md" style={{ fontFamily: "'Manrope', sans-serif" }}>
                Built for fast-moving markets. Executes on a 9 &amp; 21 strategy and thrives on low-spread broker accounts with strict micro-lot sizing.
              </p>
              <ul className="space-y-2 text-sm text-zinc-300">
                <li className="flex items-start gap-2"><CheckCircle2 size={14} className="text-emerald-400 mt-0.5 flex-shrink-0" /><span>Strategy: 9 &amp; 21 EMA execution</span></li>
                <li className="flex items-start gap-2"><CheckCircle2 size={14} className="text-emerald-400 mt-0.5 flex-shrink-0" /><span>Min $10,000 USD balance for safety</span></li>
                <li className="flex items-start gap-2"><CheckCircle2 size={14} className="text-emerald-400 mt-0.5 flex-shrink-0" /><span>Low-spread account · micro lots only</span></li>
              </ul>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

function WhyVelox() {
  const points = [
    { icon: Target, title: "Strategy-only execution", body: "No random trades. Bots fire only when 9 & 21 EMA cross and tap the 9 EMA on the 15-minute chart." },
    { icon: Activity, title: "Multi-timeframe confirmation", body: "Before entering, the engine analyses 1H and 30M action — precision over guesswork." },
    { icon: Brain, title: "Custom ATR volatility filter", body: "A proprietary Average True Range indicator measures range over a defined window, so trades respect real market volatility." },
    { icon: ShieldCheck, title: "Fintech-grade discipline", body: "Built like an institution-grade trading desk — emotionless, repeatable, and rule-bound 24/5." },
  ];
  return (
    <section id="why" data-testid="why-section" className="py-28 px-6 lg:px-10 relative">
      <div className="max-w-7xl mx-auto">
        <motion.div {...fadeUp} className="mb-14 max-w-3xl">
          <div className="text-[10px] tracking-[0.28em] text-[#00b4d8] mb-4" style={{ fontFamily: "'IBM Plex Mono', monospace" }}>
            WHY VELOX ALGO TECH
          </div>
          <h2 className="text-3xl md:text-5xl font-bold text-white tracking-tight leading-[1.05] mb-5" style={{ fontFamily: "'Outfit', sans-serif" }}>
            Precision, not randomness.
          </h2>
          <p className="text-base text-zinc-400 leading-relaxed" style={{ fontFamily: "'Manrope', sans-serif" }}>
            Velox Algo Tech is a fintech trading tool that refuses to take random trades. It triggers an entry only when the <span className="text-white font-semibold">9 &amp; 21 EMA cross and tap the 9 EMA on a 15-minute chart</span>, then double-checks the move on 1H and 30M time frames. A custom ATR indicator measures market volatility over a set period, and only after that full technical chain clears does the trade execute.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {points.map((p, i) => (
            <motion.div
              key={p.title}
              data-testid={`why-card-${i}`}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.6, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] }}
              className="p-7 rounded-2xl border border-white/10 bg-[#13131A]/60 hover:border-[#00b4d8]/40 transition-colors"
            >
              <div className="w-10 h-10 rounded-xl bg-[#00b4d8]/10 border border-[#00b4d8]/20 flex items-center justify-center mb-5">
                <p.icon size={18} className="text-[#00b4d8]" />
              </div>
              <h3 className="text-base font-semibold text-white mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>{p.title}</h3>
              <p className="text-sm text-zinc-400 leading-relaxed" style={{ fontFamily: "'Manrope', sans-serif" }}>{p.body}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

function TradingView() {
  return (
    <section data-testid="tradingview-section" className="py-28 px-6 lg:px-10">
      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
        <motion.div {...fadeUp} className="lg:col-span-5">
          <div className="text-[10px] tracking-[0.28em] text-[#00b4d8] mb-4" style={{ fontFamily: "'IBM Plex Mono', monospace" }}>
            TRADINGVIEW INTEGRATION
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-white tracking-tight leading-[1.05] mb-5" style={{ fontFamily: "'Outfit', sans-serif" }}>
            Supercharts + custom indicators,<br/>with an AI copilot.
          </h2>
          <p className="text-base text-zinc-400 leading-relaxed mb-6" style={{ fontFamily: "'Manrope', sans-serif" }}>
            Native TradingView Supercharts embedded into your workspace. Overlay exclusive Velox indicators that flag high-probability setups, then let the AI toolkit filter, suggest, and execute.
          </p>
          <div className="flex flex-col gap-3">
            {[
              "Professional-grade charting, zero extra subscription",
              "Proprietary indicators — Velox Liquidity, Smart Range, AI Trend",
              "Manual trading mode with AI discipline filters",
            ].map((x) => (
              <div key={x} className="flex items-start gap-2.5 text-sm text-zinc-300">
                <CheckCircle2 size={16} className="text-[#00b4d8] mt-0.5 flex-shrink-0" />
                <span style={{ fontFamily: "'Manrope', sans-serif" }}>{x}</span>
              </div>
            ))}
          </div>
        </motion.div>

        <motion.div {...fadeUp} className="lg:col-span-7">
          <div data-testid="tradingview-mockup-image" className="relative rounded-2xl border border-white/10 bg-[#13131A]/80 backdrop-blur-md overflow-hidden shadow-[0_40px_100px_-20px_rgba(125,211,252,0.15)]">
            {/* browser chrome */}
            <div className="flex items-center gap-1.5 px-4 py-2.5 border-b border-white/5 bg-[#0b132b]/60">
              <div className="w-2.5 h-2.5 rounded-full bg-rose-500/60" />
              <div className="w-2.5 h-2.5 rounded-full bg-amber-500/60" />
              <div className="w-2.5 h-2.5 rounded-full bg-emerald-500/60" />
              <div className="ml-4 text-[10px] text-zinc-500" style={{ fontFamily: "'IBM Plex Mono', monospace" }}>
                veloxalgotech.com / charts / EURUSD
              </div>
            </div>
            {/* fake chart */}
            <div className="relative aspect-[16/10] p-5">
              <svg className="w-full h-full" viewBox="0 0 800 480">
                {/* grid */}
                {Array.from({ length: 8 }).map((_, i) => (
                  <line key={`h${i}`} x1="0" x2="800" y1={i * 60} y2={i * 60} stroke="rgba(255,255,255,0.04)" />
                ))}
                {Array.from({ length: 12 }).map((_, i) => (
                  <line key={`v${i}`} y1="0" y2="480" x1={i * 66} x2={i * 66} stroke="rgba(255,255,255,0.04)" />
                ))}
                {/* candles */}
                {Array.from({ length: 40 }).map((_, i) => {
                  const up = (i * 37) % 7 > 3;
                  const base = 240 + Math.sin(i * 0.55) * 90 + (i - 20) * 2;
                  const h = 12 + (i % 7) * 4;
                  return (
                    <g key={i}>
                      <line x1={20 + i * 18} x2={20 + i * 18} y1={base - h - 8} y2={base + h + 8} stroke={up ? "#0eae7a" : "#c94a4a"} strokeWidth="1" opacity="0.7" />
                      <rect x={14 + i * 18} y={base - h} width="12" height={h * 2} fill={up ? "#0eae7a" : "#c94a4a"} opacity="0.85" />
                    </g>
                  );
                })}
                {/* trend line */}
                <polyline points="0,360 80,340 160,310 240,290 320,270 400,250 480,220 560,210 640,180 720,140 800,120" fill="none" stroke="#00b4d8" strokeWidth="1.5" opacity="0.9" />
                {/* entry marker */}
                <circle cx="480" cy="220" r="6" fill="#00b4d8" />
                <text x="495" y="215" fill="#00b4d8" fontSize="10" fontFamily="'IBM Plex Mono'">AI ENTRY · 1.0824</text>
              </svg>
              <div className="absolute top-6 left-6 px-2 py-0.5 rounded bg-[#00b4d8]/15 border border-[#00b4d8]/30">
                <span className="text-[10px] tracking-widest text-[#00b4d8]" style={{ fontFamily: "'IBM Plex Mono', monospace" }}>EURUSD · H1</span>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

const COMP_ROWS = [
  { feature: "Execution speed", manual: "Slow · human reaction", ai: "Instant · algorithmic", key: "execution" },
  { feature: "Strategy discipline", manual: "Greed, fatigue, FOMO", ai: "Rules-only, always", key: "discipline" },
  { feature: "Consistency", manual: "Fluctuates by mood", ai: "Repeatable, rules-based", key: "consistency" },
  { feature: "24/5 availability", manual: "Only when you're online", ai: "Zero downtime", key: "availability" },
  { feature: "Setup ease", manual: "Steep learning curve", ai: "AI Presets, one-click", key: "setup" },
];

function Comparison() {
  return (
    <section data-testid="comparison-table-section" className="py-28 px-6 lg:px-10">
      <div className="max-w-5xl mx-auto">
        <motion.div {...fadeUp} className="mb-12 text-center">
          <div className="text-[10px] tracking-[0.28em] text-[#00b4d8] mb-4" style={{ fontFamily: "'IBM Plex Mono', monospace" }}>
            WHY AUTOMATION WINS
          </div>
          <h2 className="text-3xl md:text-5xl font-bold text-white tracking-tight leading-[1.05]" style={{ fontFamily: "'Outfit', sans-serif" }}>
            Manual trading vs <span className="text-[#00b4d8]">Velox AI</span>.
          </h2>
        </motion.div>

        <motion.div {...fadeUp} className="rounded-2xl border border-white/10 bg-[#13131A]/60 backdrop-blur-sm overflow-hidden">
          <div className="grid grid-cols-12 px-6 py-4 border-b border-white/10 text-[10px] tracking-[0.25em] text-zinc-500" style={{ fontFamily: "'IBM Plex Mono', monospace" }}>
            <div className="col-span-4">FEATURE</div>
            <div className="col-span-4">MANUAL</div>
            <div className="col-span-4 text-[#00b4d8]">VELOX AI</div>
          </div>
          {COMP_ROWS.map((r, i) => (
            <motion.div
              key={r.key}
              data-testid={`comparison-row-${r.key}`}
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.5, delay: i * 0.06 }}
              className="grid grid-cols-12 px-6 py-5 border-b border-white/5 last:border-b-0 items-center text-sm"
            >
              <div className="col-span-4 text-white font-medium" style={{ fontFamily: "'Outfit', sans-serif" }}>{r.feature}</div>
              <div className="col-span-4 flex items-center gap-2 text-zinc-500">
                <XCircle size={15} className="text-rose-500/70 flex-shrink-0" />
                <span style={{ fontFamily: "'Manrope', sans-serif" }}>{r.manual}</span>
              </div>
              <div className="col-span-4 flex items-center gap-2 text-zinc-200">
                <CheckCircle2 size={15} className="text-[#00b4d8] flex-shrink-0" />
                <span style={{ fontFamily: "'Manrope', sans-serif" }}>{r.ai}</span>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

const FAQ_ITEMS = [
  { q: "Do I need to know coding or technical analysis?", a: "No. Both bots run on pre-defined, fintech-grade strategies — MTF AI on 1H + 30M (9 & 27 + custom ATR) and Scalping AI on 9 & 21 EMA. Pick a bot, link your broker, deploy." },
  { q: "Is my money safe?", a: "Velox never holds your funds. Your capital stays inside your broker account; we connect via secure, read/trade API with no withdrawal permission." },
  { q: "How much do I need to start?", a: "MTF AI runs on a USC (CENT) account with lot sizing at 1% of your micro-account capital. Scalping AI requires a USD account with a minimum $10,000 balance for safety." },
  { q: "Which brokers are supported?", a: "Exness, XM, and Vantage out of the box — all our official partners. Pro subscribers can connect any supported MT5-compatible broker." },
  { q: "Can I cancel anytime?", a: "Yes. Monthly Pro subscription cancels immediately at the end of the current cycle. The ₹2,999 one-time platform fee is non-refundable once the account is created — see Refund Policy." },
];

function FAQ() {
  const [open, setOpen] = useState(0);
  return (
    <section id="faq" data-testid="faq-section" className="py-28 px-6 lg:px-10">
      <div className="max-w-3xl mx-auto">
        <motion.div {...fadeUp} className="mb-10">
          <div className="text-[10px] tracking-[0.28em] text-[#00b4d8] mb-4" style={{ fontFamily: "'IBM Plex Mono', monospace" }}>
            FREQUENTLY ASKED
          </div>
          <h2 className="text-3xl md:text-5xl font-bold text-white tracking-tight leading-[1.05]" style={{ fontFamily: "'Outfit', sans-serif" }}>
            Questions, answered.
          </h2>
        </motion.div>

        <div data-testid="faq-accordion" className="rounded-2xl border border-white/10 bg-[#13131A]/50 backdrop-blur-sm overflow-hidden">
          {FAQ_ITEMS.map((item, i) => {
            const isOpen = open === i;
            return (
              <div key={i} className="border-b border-white/5 last:border-b-0">
                <button
                  onClick={() => setOpen(isOpen ? -1 : i)}
                  data-testid={`faq-item-${i}`}
                  className="w-full flex items-center justify-between gap-4 px-6 py-5 text-left hover:bg-white/[0.02] transition-colors"
                >
                  <span className="text-sm md:text-base text-white font-medium" style={{ fontFamily: "'Outfit', sans-serif" }}>{item.q}</span>
                  <ChevronDown
                    size={18}
                    className={`text-[#00b4d8] flex-shrink-0 transition-transform ${isOpen ? "rotate-180" : ""}`}
                  />
                </button>
                <div
                  className={`grid transition-all duration-400 ease-out ${isOpen ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"}`}
                >
                  <div className="overflow-hidden">
                    <p className="px-6 pb-5 text-sm text-zinc-400 leading-relaxed" style={{ fontFamily: "'Manrope', sans-serif" }}>
                      {item.a}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function CTABand() {
  const navigate = useNavigate();
  return (
    <section className="px-6 lg:px-10 py-12">
      <div className="max-w-6xl mx-auto relative rounded-3xl overflow-hidden border border-[#00b4d8]/30 bg-gradient-to-br from-[#00b4d8]/[0.12] via-[#13131A] to-[#0b132b] p-10 md:p-16 text-center">
        <div className="absolute inset-0 opacity-30" style={{ background: "radial-gradient(ellipse at center, rgba(125,211,252,0.25), transparent 60%)" }} />
        <div className="relative">
          <Rocket size={32} className="text-[#00b4d8] mx-auto mb-5" />
          <h2 className="text-3xl md:text-5xl font-bold text-white mb-4 tracking-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>
            Stop guessing. <span className="text-[#00b4d8]">Start executing.</span>
          </h2>
          <p className="text-base text-zinc-400 max-w-xl mx-auto mb-8" style={{ fontFamily: "'Manrope', sans-serif" }}>
            Pay the one-time ₹2,999 platform fee, link your broker, and let MTF + Scalping AI take over.
          </p>
          <button
            onClick={() => navigate("/affiliate/login")}
            className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-[#00b4d8] hover:bg-[#0077b6] text-[#0b132b] font-semibold text-sm transition-all shadow-[0_0_40px_rgba(125,211,252,0.4)]"
          >
            Get Started
            <ArrowRight size={16} />
          </button>
        </div>
      </div>
    </section>
  );
}

function Partners() {
  return (
    <section data-testid="partners-section" className="py-16 px-6 lg:px-10 border-t border-white/5">
      <div className="max-w-6xl mx-auto">
        <motion.div {...fadeUp} className="text-center mb-10">
          <div className="text-[10px] tracking-[0.28em] text-[#00b4d8] mb-3" style={{ fontFamily: "'IBM Plex Mono', monospace" }}>
            OUR PARTNERS
          </div>
          <h2 className="text-2xl md:text-3xl font-bold text-white tracking-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>
            Trade with brokers we trust.
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {PARTNERS.map((p) => (
            <motion.a
              key={p.name}
              {...fadeUp}
              href={p.url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`partner-card-${p.name.toLowerCase()}`}
              className="group p-7 rounded-2xl border border-white/10 bg-[#13131A]/60 hover:border-[#00b4d8]/40 hover:bg-[#00b4d8]/[0.04] transition-all flex flex-col items-center text-center"
            >
              <div
                className="text-2xl md:text-3xl font-extrabold tracking-[0.2em] text-white mb-3 group-hover:text-[#00b4d8] transition-colors"
                style={{ fontFamily: "'Outfit', sans-serif" }}
              >
                {p.name}
              </div>
              <div
                className="text-[10px] tracking-[0.25em] text-zinc-500 group-hover:text-zinc-300 mb-4"
                style={{ fontFamily: "'IBM Plex Mono', monospace" }}
              >
                {p.note}
              </div>
              <div className="inline-flex items-center gap-1.5 text-[11px] text-[#00b4d8] opacity-80 group-hover:opacity-100">
                Open broker
                <ArrowRight size={12} className="group-hover:translate-x-0.5 transition-transform" />
              </div>
            </motion.a>
          ))}
        </div>
      </div>
    </section>
  );
}


function TraderMatrix() {
  const items = [
    { letter: "T", label: "TIME",       quote: "Your time is limited, so don't waste it living someone else's life.", who: "STEVE JOBS" },
    { letter: "R", label: "RISK",       quote: "The biggest risk in life is not taking any risk.",                    who: "MARK ZUCKERBERG" },
    { letter: "A", label: "ANALYSIS",   quote: "Price is what you pay. Value is what you get.",                       who: "WARREN BUFFETT" },
    { letter: "D", label: "DISCIPLINE", quote: "The big money is not in the buying and selling, but in the waiting.", who: "CHARLIE MUNGER" },
    { letter: "E", label: "EMOTION",    quote: "If investing is entertaining for you, if it's fun, you're probably not making any money.", who: "GEORGE SOROS" },
    { letter: "R", label: "REWARDS",    quote: "Pain + Reflection = Progress.",                                        who: "RAY DALIO" },
  ];
  return (
    <section
      id="mindset"
      data-testid="trader-matrix"
      className="relative py-28 md:py-40 px-6 lg:px-10 overflow-hidden"
    >
      {/* Premium grid + glow backdrop */}
      <div aria-hidden className="absolute inset-0 pointer-events-none opacity-[0.06]" style={{
        backgroundImage: "linear-gradient(rgba(0,229,255,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(0,229,255,0.6) 1px, transparent 1px)",
        backgroundSize: "56px 56px",
        maskImage: "radial-gradient(ellipse at center, black 40%, transparent 80%)",
        WebkitMaskImage: "radial-gradient(ellipse at center, black 40%, transparent 80%)",
      }} />
      <div aria-hidden className="absolute -top-24 left-1/2 -translate-x-1/2 w-[900px] h-[900px] rounded-full pointer-events-none" style={{
        background: "radial-gradient(circle, rgba(0,180,216,0.10) 0%, transparent 60%)",
      }} />

      <div className="relative max-w-6xl mx-auto">
        <div className="text-center mb-16">
          <p className="inline-block px-3 py-1 rounded-full text-[10px] font-bold tracking-[0.4em] mb-6 text-[#00b4d8] border border-[#00b4d8]/30 bg-[#00b4d8]/[0.06]"
             style={{ fontFamily: "'IBM Plex Mono', monospace" }}>
            THE ANATOMY OF A MATRIX
          </p>
          <h2 className="text-5xl md:text-7xl lg:text-8xl font-black tracking-tighter text-white leading-none"
              style={{ fontFamily: "'Cabinet Grotesk', 'Outfit', sans-serif", letterSpacing: "-0.03em" }}>
            T<span className="text-[#00b4d8]">.</span>R<span className="text-[#00b4d8]">.</span>A<span className="text-[#00b4d8]">.</span>D<span className="text-[#00b4d8]">.</span>E<span className="text-[#00b4d8]">.</span>R
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {items.map((item, i) => (
            <div
              key={i}
              data-testid={`trader-card-${item.label.toLowerCase()}`}
              className="group relative p-6 rounded-2xl transition-all duration-300 hover:-translate-y-1"
              style={{
                background: "linear-gradient(180deg, rgba(255,255,255,0.04) 0%, rgba(255,255,255,0.01) 100%)",
                border: "1px solid rgba(255,255,255,0.08)",
                backdropFilter: "blur(12px)",
                WebkitBackdropFilter: "blur(12px)",
              }}
            >
              {/* Hover glow */}
              <div aria-hidden className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" style={{
                background: "radial-gradient(circle at top left, rgba(0,180,216,0.14), transparent 60%)",
                boxShadow: "0 0 40px rgba(0,180,216,0.15)",
              }} />
              <div className="relative flex items-start gap-4">
                <div
                  className="flex-shrink-0 w-14 h-14 rounded-xl flex items-center justify-center text-3xl font-black text-[#00b4d8]"
                  style={{
                    background: "linear-gradient(135deg, rgba(0,229,255,0.18), rgba(6,214,160,0.10))",
                    border: "1px solid rgba(0,180,216,0.5)",
                    boxShadow: "inset 0 1px 0 rgba(255,255,255,0.08), 0 8px 24px rgba(0,180,216,0.10)",
                    fontFamily: "'Cabinet Grotesk', sans-serif",
                  }}
                >
                  {item.letter}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[10px] font-bold tracking-[0.28em] mb-2.5 text-[#00b4d8]" style={{ fontFamily: "'IBM Plex Mono', monospace" }}>
                    {item.label}
                  </p>
                  <p className="text-[15px] italic leading-relaxed mb-3 text-zinc-100" style={{ fontFamily: "'Manrope', sans-serif" }}>
                    &ldquo;{item.quote}&rdquo;
                  </p>
                  <p className="text-[10px] font-bold tracking-[0.18em] text-zinc-500" style={{ fontFamily: "'IBM Plex Mono', monospace" }}>
                    — {item.who}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div
          className="mt-12 rounded-3xl p-10 md:p-16 text-center relative overflow-hidden"
          style={{
            background: "linear-gradient(135deg, rgba(0,180,216,0.12), rgba(6,214,160,0.06))",
            border: "1px solid rgba(0,180,216,0.4)",
            boxShadow: "0 20px 60px rgba(0,180,216,0.15), inset 0 1px 0 rgba(255,255,255,0.08)",
          }}
          data-testid="trader-tagline"
        >
          <div aria-hidden className="absolute inset-0 opacity-[0.04] pointer-events-none" style={{
            backgroundImage: "linear-gradient(rgba(255,255,255,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.6) 1px, transparent 1px)",
            backgroundSize: "40px 40px",
          }} />
          <p className="relative text-2xl md:text-4xl lg:text-5xl font-black tracking-tight text-white leading-tight" style={{ fontFamily: "'Cabinet Grotesk', 'Outfit', sans-serif", letterSpacing: "-0.02em" }}>
            TRADER: <span className="text-[#00b4d8]">NOT JUST A PROFESSION.</span> IT&apos;S A MINDSET.
          </p>
        </div>
      </div>
    </section>
  );
}


function LearnEarnFlow() {
  const steps = [
    {
      n: "01",
      title: "Register with VeloxAlgoTech",
      body: "Sign up for a free VeloxAlgoTech account to explore our trading indicators. Study the setups, timing rules, and signal logic on live charts before committing capital.",
    },
    {
      n: "02",
      title: "Connect your Trading API",
      body: "Once you're confident, link your MT5 broker account via our secure API. Every signal is fully transparent — you'll always know why the trade was taken and why the trade was closed.",
    },
    {
      n: "03",
      title: "Learn First, Earn After",
      body: "Master the indicators, understand the process, then let the automation do the heavy lifting. Discipline compounds — capital follows.",
    },
  ];
  return (
    <section id="learn" data-testid="learn-earn-flow" className="relative py-28 md:py-40 px-6 lg:px-10 overflow-hidden">
      <div aria-hidden className="absolute -bottom-24 right-1/2 translate-x-1/2 w-[900px] h-[600px] pointer-events-none" style={{
        background: "radial-gradient(ellipse at center, rgba(6,214,160,0.06) 0%, transparent 60%)",
      }} />
      <div className="relative max-w-6xl mx-auto">
        <div className="text-center mb-16">
          <p className="inline-block px-3 py-1 rounded-full text-[10px] font-bold tracking-[0.4em] mb-6 text-[#00b4d8] border border-[#00b4d8]/30 bg-[#00b4d8]/[0.06]"
             style={{ fontFamily: "'IBM Plex Mono', monospace" }}>
            HOW TO GET STARTED
          </p>
          <h2 className="text-4xl md:text-6xl lg:text-7xl font-black tracking-tight text-white leading-none"
              style={{ fontFamily: "'Cabinet Grotesk', 'Outfit', sans-serif", letterSpacing: "-0.03em" }}>
            Explore the Indicators.<br />
            <span className="text-[#00b4d8]">Learn.</span> Then Earn.
          </h2>
          <p className="max-w-2xl mx-auto text-base md:text-lg text-zinc-400 mt-6 leading-relaxed" style={{ fontFamily: "'Manrope', sans-serif" }}>
            No black boxes. Every signal we send is explainable — trained traders always know why a trade opens and why it closes.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {steps.map((s) => (
            <div
              key={s.n}
              data-testid={`learn-step-${s.n}`}
              className="group relative p-8 rounded-2xl transition-all duration-300 hover:-translate-y-1"
              style={{
                background: "linear-gradient(180deg, rgba(255,255,255,0.04) 0%, rgba(255,255,255,0.01) 100%)",
                border: "1px solid rgba(255,255,255,0.08)",
                backdropFilter: "blur(12px)",
                WebkitBackdropFilter: "blur(12px)",
              }}
            >
              <div aria-hidden className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" style={{
                background: "radial-gradient(circle at top left, rgba(0,180,216,0.14), transparent 60%)",
                boxShadow: "0 0 40px rgba(0,180,216,0.15)",
              }} />
              <div
                className="absolute -top-3 left-6 px-3 py-1 rounded-full text-[10px] font-bold tracking-[0.28em] bg-[#0b132b] border border-[#00b4d8]/60 text-[#00b4d8]"
                style={{ fontFamily: "'IBM Plex Mono', monospace" }}
              >
                STEP {s.n}
              </div>
              <h3 className="relative text-xl md:text-2xl font-black text-white mt-3 mb-3 tracking-tight" style={{ fontFamily: "'Cabinet Grotesk', 'Outfit', sans-serif", letterSpacing: "-0.01em" }}>
                {s.title}
              </h3>
              <p className="relative text-[15px] leading-relaxed text-zinc-400" style={{ fontFamily: "'Manrope', sans-serif" }}>
                {s.body}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}


export default function VeloxLanding() {
  return (
    <div className="min-h-screen bg-[#0b132b] text-white" data-testid="velox-landing">
      <Nav />
      <Hero />
      <BrokersStrip />
      <HowItWorks />
      <WhyVelox />
      <StrategyCards />
      <TraderMatrix />
      <LearnEarnFlow />
      <TradingView />
      <Comparison />
      <FAQ />
      <CTABand />
      <Partners />
    </div>
  );
}
